/**
 * Catches a render error so one broken page doesn't blank the app. It sits around the routed content, so the
 * header and nav stay usable, and resets when the path changes. A stale chunk after a deploy reloads once instead.
 * Error details show in dev only; people see plain words and three ways out.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useNav } from '../nav'
import { Button } from './Button'
import { Inline } from './Inline'
import { isChunkError, reloadOnce } from './staleChunk'
import s from './ErrorBoundary.module.css'

interface InnerProps {
  resetKey: string
  fallback: (error: Error, reset: () => void, reloading: boolean) => ReactNode
  onError?: (error: Error, info: ErrorInfo) => void
  /** Called for a stale chunk; returns whether it reloaded. Defaults to reloadOnce (sessionStorage cooldown). */
  onStaleChunk?: () => boolean
  children: ReactNode
}

interface InnerState {
  error: Error | null
  /** The resetKey the error happened under; a new key clears it. */
  at: string
  reloading: boolean
}

export class ErrorBoundaryCore extends Component<InnerProps, InnerState> {
  state: InnerState = { error: null, at: '', reloading: false }

  static getDerivedStateFromError(e: unknown): Partial<InnerState> {
    return { error: e instanceof Error ? e : new Error(String(e)) }
  }

  static getDerivedStateFromProps(props: InnerProps, state: InnerState): Partial<InnerState> | null {
    // Moving to another page clears the error: the new page gets its own chance.
    if (state.error && state.at && props.resetKey !== state.at) return { error: null, at: '', reloading: false }
    return null
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const reloading = isChunkError(error) && (this.props.onStaleChunk ?? reloadOnce)()
    this.setState({ at: this.props.resetKey, reloading })
    this.props.onError?.(error, info)
  }

  reset = () => this.setState({ error: null, at: '', reloading: false })

  render() {
    return this.state.error ? this.props.fallback(this.state.error, this.reset, this.state.reloading) : this.props.children
  }
}

export function ErrorBoundary({ children, resetKey, onError }: { children: ReactNode; /** Defaults to the current path. */ resetKey?: string; onError?: (error: Error, info: ErrorInfo) => void }) {
  const { where } = useNav()
  return (
    <ErrorBoundaryCore resetKey={resetKey ?? where.pathname} onError={onError} fallback={(error, reset, reloading) => <ErrorFallback error={error} onRetry={reset} reloading={reloading} />}>
      {children}
    </ErrorBoundaryCore>
  )
}

/** What a person sees when a page broke. A stale chunk reloads once; after that it says a newer version is out. */
export function ErrorFallback({ error, onRetry, reloading, dev = import.meta.env.DEV }: { error: Error; onRetry: () => void; /** A reload onto the new build is under way. */ reloading?: boolean; dev?: boolean }) {
  const stale = isChunkError(error)
  if (reloading) {
    return (
      <div className={s.box} role="status">
        <h2 className={s.title}>Updating to the latest version.</h2>
      </div>
    )
  }
  return (
    <div className={s.box} role="alert">
      <h2 className={s.title}>{stale ? 'A newer version is out.' : "This page didn't load."}</h2>
      <p className={s.body}>
        {stale
          ? 'cpg.computer changed since this tab opened. Reload to get the new version.'
          : 'Something went wrong while drawing it. The rest of cpg.computer still works, and nothing you saved is lost.'}
      </p>
      <Inline>
        {!stale && (
          <Button variant="ink" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )}
        <Button size="sm" onClick={() => history.back()}>
          Go back
        </Button>
        <Button size="sm" variant={stale ? 'ink' : 'ghost'} onClick={() => location.reload()}>
          Reload
        </Button>
      </Inline>
      {dev && (
        <pre className={s.dev}>
          {error.name}: {error.message}
          {error.stack ? `\n\n${error.stack}` : ''}
        </pre>
      )}
    </div>
  )
}
